import * as React from 'react';
import { createContext, useContext } from 'react';

import { useLayer } from '@coursera-web/statsig';

/** Variant groups configured on the `ai-series-mvp` experiment (MERCH-3512). */
export const AI_SERIES_CONTROL_VARIANT = 'control';
export const AI_SERIES_TEST_VARIANT = 'test';

type AiSeriesContextType = {
  /** Whether this learner is in the AI Series treatment group. */
  showAiSeries: boolean;
};

const AiSeriesContext = createContext<AiSeriesContextType>({
  showAiSeries: false,
});

export const useAiSeriesContext = () => useContext(AiSeriesContext);

/**
 * Whether the AI Series experience should be shown to this learner.
 *
 * Returns `false` outside `AiSeriesProvider`, so an unwrapped tree degrades to the
 * control experience rather than throwing.
 */
export const useShowAiSeries = (): boolean => useAiSeriesContext().showAiSeries;

/**
 * Reads the AI Series assignment straight from the `ai-series-mvp-layer` layer, without
 * needing a provider above it.
 *
 * Prefer `useShowAiSeries` when several components in a subtree need the assignment —
 * the provider reads the layer once for all of them. Reach for this hook when a single
 * component needs it, as on the logged-out home page.
 *
 * When a surface can only show the experience to some of its visitors — e.g. the role
 * description page, where only a few roles map to a series — pass `enabled: false` for
 * the rest so they are not exposed.
 *
 * Requires a `StatsigProvider` above it in the tree.
 */
export const useIsAiSeriesTreatment = ({ enabled = true }: { enabled?: boolean } = {}): boolean => {
  const layer = useLayer('ai-series-mvp-layer');

  return enabled && layer.get('variant', AI_SERIES_CONTROL_VARIANT) === AI_SERIES_TEST_VARIANT;
};

type Props = {
  children: React.ReactNode;
};

/**
 * Provides the AI Series MVP experiment assignment from the `ai-series-mvp-layer`
 * Statsig layer (MERCH-3512).
 *
 * Mounting this provider reads the layer, which logs a Statsig impression — so mount
 * it at the point a learner qualifies for the AI Series experience, not at the app
 * root. Mounting it above learners who could never see the experience inflates the
 * experiment's denominator and dilutes the measured effect.
 *
 * Any variant other than `test` (including an unrecognized group added in the Console
 * before the code knows about it) resolves to `false`.
 *
 * Requires a `StatsigProvider` above it in the tree.
 */
const AiSeriesProvider = ({ children }: Props) => {
  const showAiSeries = useIsAiSeriesTreatment();

  const value = React.useMemo(() => ({ showAiSeries }), [showAiSeries]);

  return <AiSeriesContext.Provider value={value}>{children}</AiSeriesContext.Provider>;
};

export default AiSeriesProvider;
